import * as Three from 'three'
import { Collada, ColladaLoader } from "three/examples/jsm/Addons.js"
import { mat4, vec4 } from './VectorMath.ts'

/**
 * Immutable representation of a posable mesh, including the mesh, the skeleton (bones), and binding data.
 */
export class Mannequin {
  constructor(
    readonly mesh: Mesh,
    readonly skeleton: Skeleton,
    readonly skinBinding: SkinBinding,
  ) { }

  static async load(url: string): Promise<Mannequin> {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to load mesh from ${url}: ${response.status} ${response.statusText}`)
    }
    let text = await response.text()

    // Remove extraneous whitespace to make it easier to step through the parser in the debugger.
    text = text.replace(/>\s+?</g, '><')

    const collada: Collada = new ColladaLoader().parse(text, url)
    const scene = collada.scene
    // scene.rotation.set(0, 0, 0)
    scene.updateMatrixWorld()
    const threeMesh = firstSkinnedMesh(scene)
    if (!threeMesh) {
      throw new Error(`No SkinnedMesh found in COLLADA ${url}`)
    }

    const textureMap = await textureMapDetailOfThreeMesh(threeMesh, collada, url)

    const mesh = Mesh.fromThreeSkinnedMesh(threeMesh, textureMap)
    const skeleton = Skeleton.fromThreeSkinnedMesh(threeMesh)
    const skinBinding: SkinBinding = {
      boneIndexes: Int32Array.from(threeMesh.geometry.attributes.skinIndex.array),
      boneWeights: threeMesh.geometry.attributes.skinWeight.array as Float32Array,
      bindFromMesh: mat4.columnMajor(...threeMesh.bindMatrix.toArray()),
    }

    return new Mannequin(mesh, skeleton, skinBinding)
  }
}

interface TextureMapDetail {
  image: HTMLImageElement
  uvData: Float32Array
}

async function textureMapDetailOfThreeMesh(
  threeMesh: Three.SkinnedMesh,
  collada: Collada,
  baseUrl: string
): Promise<TextureMapDetail | null> {
  const uvBuffer = threeMesh.geometry.attributes.uv
  if (
    !uvBuffer
    || !(uvBuffer instanceof Three.Float32BufferAttribute)
    || uvBuffer.itemSize !== 2
    || !(uvBuffer.array instanceof Float32Array)
  ) {
    return null
  }

  if (threeMesh.material?.constructor !== Three.MeshLambertMaterial) {
    return null
  }

  const library: any = collada.library
  const materialsLibrary: any = library.materials
  const material: any = Object.values(materialsLibrary).find((m: any) => m.build === threeMesh.material)
  if (!material) {
    return null
  }

  const effectsLibrary: any = library.effects
  const effect: any = effectsLibrary[material.url]?.profile
  if (!effect || !effect.samplers || !effect.surfaces) {
    return null
  }

  if (effect.technique?.type !== 'lambert') {
    return null
  }

  const samplerId = effect.technique.parameters?.diffuse?.texture?.id
  if (samplerId == undefined) {
    return null
  }

  const surfaceId = effect.samplers[samplerId]?.source
  if (surfaceId == undefined) {
    return null
  }

  const imageId = effect.surfaces[surfaceId]?.init_from
  if (imageId == undefined) {
    return null
  }

  const imageLibrary: any = library.images
  const imageFilename: any = imageLibrary[imageId]?.init_from
  if (!imageFilename) {
    return null
  }

  const imageUrl = new URL(imageFilename, new URL(baseUrl, window.location.href))

  const image = new Image()
  try {
    image.src = imageUrl.href
    await image.decode()
  } catch (e) {
    console.log(`error loading texture map image from ${imageUrl}: ${e}`)
    return null
  }

  return {
    image,
    uvData: uvBuffer.array
  }
}

export class Mesh {
  readonly positions: Float32Array
  readonly normals: Float32Array
  readonly textureMap: TextureMapDetail | null

  constructor(props: {
    positions: Float32Array
    normals: Float32Array
    textureMap: TextureMapDetail | null
  }) {
    this.positions = props.positions
    this.normals = props.normals
    this.textureMap = props.textureMap
  }

  static fromThreeSkinnedMesh(
    mesh: Three.SkinnedMesh,
    textureMap: TextureMapDetail | null = null
  ): Mesh {
    const attributes = mesh.geometry.attributes
    const positions = (attributes.position.array as Float32Array)
    const normals = (attributes.normal.array as Float32Array)

    return new Mesh({
      positions,
      normals,
      textureMap
    })
  }
}

export const nullMesh = new Mesh({
  positions: new Float32Array(),
  normals: new Float32Array(),
  textureMap: null,
})

function firstSkinnedMesh(object: Three.Object3D): Three.SkinnedMesh | null {
  if (object instanceof Three.SkinnedMesh) {
    return object
  }

  for (let child of object.children) {
    const found = firstSkinnedMesh(child)
    if (found) {
      return found
    }
  }

  return null
}

export class Skeleton {
  // All bones, in order so that bone attachment indexes are correct.
  readonly bones: Bone[]

  // All bones in parent-before-children order.
  readonly topologicalOrder: number[]

  constructor(props: {
    bones: Bone[]
    topologicalOrder: number[]
  }) {
    this.bones = props.bones
    this.topologicalOrder = props.topologicalOrder
  }

  static fromThreeSkinnedMesh(threeMesh: Three.SkinnedMesh): Skeleton {
    const threeBones = threeMesh.skeleton.bones
    const threeBoneInverses = threeMesh.skeleton.boneInverses

    // threeBones is in the following order:
    //
    // - All bones listed in the `<Name_array>` of the `<skin>` node, in that order.
    // - Any bones in the <visual_scene> hierarchy that were not already included, parents before children.
    //
    // The bone attachment indexes are determined by the order of the bones in `<Name_array>`. The other bones in the `<visual_scene>` have no attachments.
    //
    // Because each bone's pose affects its children, I want to traverse the bones in topological order, parents before children, to compute all `worldFromBone` transforms with minimal bookkeeping.

    // This will be the bone indexes in topological order.
    let topologicalOrder: number[] = []

    // Map each `Three.Bone` object to its index.
    const boneIndexes: Map<Three.Object3D, number> = new Map(threeBones.map((bone, i) => [bone, i]))

    // A bone whose parent isn't also a bone is a root. Walk each root's tree.
    threeBones.forEach(candidate => {
      if (candidate.parent && boneIndexes.has(candidate.parent)) {
        // Not a root.
        return
      }

      let stack: [Three.Bone] = [candidate]
      while (stack.length > 0) {
        const bone = stack.pop()!
        const i = boneIndexes.get(bone)
        if (i === undefined) {
          throw new Error(`I don't know what to do about extraneous bone ${bone.name}.`)
        }
        topologicalOrder.push(i)
        // The reversal is unnecessary but simplifies debugging.
        for (const child of bone.children.slice().reverse()) {
          if (child instanceof Three.Bone) {
            stack.push(child)
          }
        }
      }
    })

    const bones = threeBones.map((bone, b) => {
      const boneFromBind = mat4.columnMajor(...threeBoneInverses[b].elements)

      const length = lengthOfThreeBone({
        b,
        attributes: threeMesh.geometry.attributes,
        boneFromBind,
        bindFromMesh: mat4.columnMajor(...threeMesh.bindMatrix.toArray())
      })

      const parentIndex = bone.parent ? (boneIndexes.get(bone.parent) ?? -1) : -1

      // A root bone's parent is the world for my purposes, but not necessarily in the Three scene, so for a root bone I need to use its `matrixWorld` instead of its `matrix` as its `parentFromBone`.
      const parentFromBone = mat4.columnMajor(...(parentIndex === -1 ? bone.matrixWorld : bone.matrix).elements)

      return new Bone({ boneFromBind, parentFromBone, parentIndex, length })
    })

    return new Skeleton({ bones, topologicalOrder })
  }

}

export const nullSkeleton = new Skeleton({
  bones: [],
  topologicalOrder: [],
})

export class Bone {
  /**
   * Transform bind space to my space.
   */
  readonly boneFromBind: mat4

  /**
   * Transform my space to my parent's space. If I am a root bone, my parent is the world.
   */
  readonly parentFromBone: mat4

  /**
   * My parent's index in my `Skeleton`'s `bones`, or -1 if I'm a root.
   */
  readonly parentIndex: number

  /**
   * How long I should be drawn, based on the extent of the vertexes attached to me.
   */
  readonly length: number

  constructor(props: {
    boneFromBind: mat4
    parentFromBone: mat4
    parentIndex: number
    length: number
  }) {
    this.boneFromBind = props.boneFromBind
    this.parentFromBone = props.parentFromBone
    this.parentIndex = props.parentIndex
    this.length = props.length
  }

  /**
   * @returns A transform to apply to the bone mesh when drawing it to represent me.
   */
  get boneMeshTransform(): mat4 {
    const girth = Math.min(this.length, 1)
    return mat4.scale(girth, this.length, girth)
  }
}

/**
 * The details of the connection between a mesh and a skeleton.
 */
export interface SkinBinding {
  /**
   * Transform from mesh space to bind space.
   * 
   * Each vertex in the mesh is defined in mesh space. Each bone includes a `bindToBone` transform from bind space to bone space, and a `boneToParent` transform from (posed) bone space to its parent (bone or world) space. So, to deform the mesh, I need to transform each vertex from mesh space to bind space, then to bone space, then apply all of the (posed) `boneToParent` transforms up to world space. This property transforms from mesh space to bind space.
   * 
   * This is what COLLADA calls the bind-shape matrix, or BSM. See COLLADA Spec 1.4.1 2nd edition, page 4-6.
   */
  readonly bindFromMesh: mat4

  /**
   * Each four element block contains indexes of the four bones to which a vertex is attached. If the vertex is attached to fewer than four bones, the extra indexes are 0 (which is also a valid bone index).
   */
  readonly boneIndexes: Int32Array

  /**
   * Each four element block contains the weights of a vertex's attachments to the four bones indexed by the corresponding elements of `boneIndexes`. If the vertex is attached to fewer than four bones, the extra weights are 0.
   */
  readonly boneWeights: Float32Array
}

const nullSkinBinding: SkinBinding = {
  bindFromMesh: mat4.identity,
  boneIndexes: new Int32Array(),
  boneWeights: new Float32Array(),
}

export const nullMannequin = new Mannequin(nullMesh, nullSkeleton, nullSkinBinding)

/**
 * Guess a length for drawing a bone based on the vertexes attached to it.
 * 
 * @param bone The bone whose length I guess.
 * @param b The index of `bone` in the array of bones.
 * @param mesh The mesh rigged by the skeleton containing `bone`.
 * @param worldFromMesh The transform from mesh space to world space. I need this because each bone has its own transform from bone space to world space, but not from bone space to mesh space.
 * @returns 
 */
function lengthOfThreeBone(props: {
  b: number
  attributes: Three.NormalBufferAttributes
  boneFromBind: mat4
  bindFromMesh: mat4
}): number {
  const { b, attributes, boneFromBind, bindFromMesh } = props

  const boneFromMesh = boneFromBind.times(bindFromMesh)
  const psMesh = attributes.position.array as Float32Array
  const positionStep = attributes.position.itemSize
  const attachments = attributes.skinIndex.array as Float32Array
  const attachmentStep = attributes.skinIndex.itemSize
  const weights = attributes.skinWeight.array as Float32Array

  let length = 0

  for (let p = 0, a = 0, end = psMesh.length; p < end; p += positionStep, a += attachmentStep) {
    for (let i = 0; i < attachmentStep; ++i) {
      if (attachments[a + i] !== b || weights[a + i] === 0) {
        continue
      }
      const pBone = boneFromMesh.times(new vec4(psMesh[p], psMesh[p + 1], psMesh[p + 2], 1))
      length = Math.max(length, pBone.y)
    }
  }

  return length === 0 ? 1 : length
}
